import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Menu = () => {
  const { language, t } = useLanguage();
  const [menuItems, setMenuItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('starters');
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  const categories = [
    { id: 'starters', label: t('menu.categories.starters') },
    { id: 'dough', label: t('menu.categories.dough') },
    { id: 'vareniki', label: t('menu.categories.vareniki') },
    { id: 'soups', label: t('menu.categories.soups') },
    { id: 'main', label: t('menu.categories.main') },
    { id: 'grill', label: t('menu.categories.grill') },
    { id: 'sides', label: t('menu.categories.sides') },
    { id: 'desserts', label: t('menu.categories.desserts') },
  ];

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(`${API}/menu`);
        setMenuItems(response.data);
      } catch (error) {
        console.error('Error fetching menu:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, []);

  const getLocalizedName = (item) => {
    switch (language) {
      case 'en': return item.name_en;
      case 'ru': return item.name_ru;
      default: return item.name_lv;
    }
  };

  const getLocalizedDescription = (item) => {
    switch (language) {
      case 'en': return item.description_en;
      case 'ru': return item.description_ru;
      default: return item.description_lv;
    }
  };

  const filteredItems = menuItems.filter(item => item.category === activeCategory);

  return (
    <section 
      id="menu" 
      className="py-24 md:py-32 bg-[#FAF8F5]"
      data-testid="menu-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#8B2942] uppercase tracking-[0.3em] text-sm">
            {t('menu.subtitle')}
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-[#1A1A1D] mt-4">
            {t('menu.title')}
          </h2>
          <div className="section-divider mt-8" />
        </div>

        {/* Category Tabs */}
        <div 
          className="flex flex-wrap justify-center gap-2 md:gap-8 mb-12"
          data-testid="menu-categories"
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`category-tab ${activeCategory === cat.id ? 'active' : ''}`}
              data-testid={`menu-category-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Menu Items */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8" data-testid="menu-items">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="menu-card p-6">
                <div className="skeleton h-6 w-3/4 mb-4 rounded" />
                <div className="skeleton h-4 w-full mb-2 rounded" />
                <div className="skeleton h-4 w-1/4 rounded" />
              </div>
            ))
          ) : (
            filteredItems.map((item) => (
              <div 
                key={item.id} 
                className={`menu-card overflow-hidden ${item.image ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
                onClick={() => item.image && setSelectedItem(item)}
                data-testid={`menu-item-${item.id}`}
              >
                <div className={`flex ${item.image ? 'flex-col sm:flex-row' : ''}`}>
                  {/* Image */}
                  {item.image && (
                    <div className="sm:w-40 sm:h-40 w-full aspect-square sm:aspect-auto flex-shrink-0 overflow-hidden bg-[#111]">
                      <img 
                        src={item.image} 
                        alt={getLocalizedName(item)}
                        className="w-full h-full object-cover"
                        style={{ transform: `scale(${item.card_scale || 1})` }}
                        loading="lazy"
                        decoding="async"
                        fetchPriority="low"
                      />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="p-6 md:p-8 flex-1">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <h3 className="font-['Playfair_Display'] text-xl md:text-2xl text-[#1A1A1D] mb-3">
                          {getLocalizedName(item)}
                        </h3>
                        <p className="text-[#6B6B6B] text-sm md:text-base leading-relaxed">
                          {getLocalizedDescription(item)}
                        </p>
                        {item.allergens && (
                          <p className="text-[#8B2942]/60 text-xs mt-2">
                            {language === 'lv' ? 'Alergēni' : language === 'ru' ? 'Аллергены' : 'Allergens'}: {item.allergens}
                          </p>
                        )}
                      </div>
                      <span className="price-tag whitespace-nowrap">
                        €{item.price}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Note */}
        <p className="text-center text-[#6B6B6B]/60 text-sm mt-12 tracking-wide">
          {language === 'lv' && 'Cenas norādītas eiro. Alergēnu informācija pieejama pēc pieprasījuma.'}
          {language === 'en' && 'Prices in euros. Allergen information available upon request.'}
          {language === 'ru' && 'Цены указаны в евро. Информация об аллергенах доступна по запросу.'}
        </p>
      </div>

      {/* Modal */}
      {selectedItem && (
        <div 
          className="fixed inset-0 z-[300] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setSelectedItem(null)}
          data-testid="menu-modal"
        >
          <div 
            className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedItem(null)}
              className="absolute top-4 right-4 md:top-6 md:right-6 text-white/80 hover:text-white p-2 transition-colors z-10"
              data-testid="modal-close"
              aria-label="Close modal"
            >
              <X size={32} />
            </button>

            <div className="md:flex">
              {/* Image */}
              <div className="w-full aspect-square max-h-[55vh] md:w-[28rem] md:h-[28rem] md:max-h-none md:flex-shrink-0 bg-[#111] overflow-hidden">
                <img 
                  src={selectedItem.image} 
                  alt={getLocalizedName(selectedItem)}
                  className="w-full h-full object-cover"
                  style={{ transform: `scale(${selectedItem.modal_scale || 1})` }}
                  data-testid="menu-modal-image"
                />
              </div>

            {/* Content */}
            <div className="p-6 md:p-10 md:flex-1 md:flex md:flex-col md:justify-center">
              <div className="flex justify-between items-start gap-4 mb-4">
                <h3 className="font-['Playfair_Display'] text-2xl md:text-4xl text-[#1A1A1D]">
                  {getLocalizedName(selectedItem)}
                </h3>
                <span className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#8B2942] whitespace-nowrap">
                  €{selectedItem.price}
                </span>
              </div>
              <p className="text-[#6B6B6B] text-lg leading-relaxed">
                {getLocalizedDescription(selectedItem)}
              </p>
              {selectedItem.allergens && (
                <p className="text-[#8B2942]/60 text-sm mt-4">
                  {language === 'lv' ? 'Alergēni' : language === 'ru' ? 'Аллергены' : 'Allergens'}: {selectedItem.allergens}
                </p>
              )}
            </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Menu;
